import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal } from '@givebutter/util';
import { faBell, faFileExport } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { exportPledges } from 'api/pledges';
import Content from 'assets/js/components/Content';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import * as ExportDialog from 'assets/js/components/ExportDialog';
import * as PledgeActions from 'assets/js/pages/pledges/PledgeActions';
import { PledgeStatusIndicator } from 'assets/js/pages/pledges/PledgeStatusIndicator';
import { PledgesMetrics } from 'assets/js/pages/pledges/PledgesMetrics';
import { PledgesTable } from 'assets/js/pages/pledges/PledgesTable';
import { PledgesTableProvider } from 'assets/js/pages/pledges/PledgesTableProvider';
import { curriedTransformTableKeyToReadable } from 'assets/js/pages/pledges/utils';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import * as SubNav from 'components/sub-nav';
import { TransactionsSubNavTabs } from 'components/sub-nav/TransactionsSubNavTabs';
import * as Table from 'components/table';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer, isoDateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize } from 'lodash';

const Pledges = () => {
    const { newExportModal } = useFlags();
    const match = useRouteMatch();
    const history = useHistory();
    const { accountId } = useCurrentAccount();

    const { mutate } = useMutation({
        mutationFn: () => {
            return exportPledges(accountId);
        },
    });

    return (
        <Content>
            <div className={'flex flex-col gap-y-4'}>
                <SubNav.Root>
                    <SubNav.Header>
                        <SubNav.Title>Pledges</SubNav.Title>
                        <SubNav.Actions>
                            {newExportModal ? (
                                <ExportDialog.Root reportType="pledges">
                                    <ExportDialog.Trigger asChild={true}>
                                        <Button variant="outline" color="neutral">
                                            <FontAwesomeIcon icon={faFileExport} />
                                            Export
                                        </Button>
                                    </ExportDialog.Trigger>
                                </ExportDialog.Root>
                            ) : (
                                <ExportDataDialog.Root label="Pledges" onSubmit={mutate}>
                                    <ExportDataDialog.Trigger asChild={true}>
                                        <Button variant="outline" color="neutral">
                                            <FontAwesomeIcon icon={faFileExport} />
                                            Export
                                        </Button>
                                    </ExportDataDialog.Trigger>
                                </ExportDataDialog.Root>
                            )}
                            <Button
                                variant="solid"
                                onClick={() =>
                                    history.push({
                                        pathname: `${match.url}/new`,
                                    })
                                }
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                New pledge
                            </Button>
                        </SubNav.Actions>
                    </SubNav.Header>
                    <TransactionsSubNavTabs />
                </SubNav.Root>
                <FilterProvider cacheKey={'pledges'} resource={'pledges'}>
                    <PledgesMetrics />
                    <PledgesTable />
                </FilterProvider>
            </div>
        </Content>
    );
};

const WrappedPledges = () => {
    const { accountId } = useCurrentAccount();
    return (
        <TableCoreResourceProvider
            resource={'pledges'}
            initialState={{
                sortingState: [{ id: 'next_installment_date', desc: false }],
                columnVisibilityState: {
                    cancel_reason: false,
                    method: false,
                    start_at: false,
                    first_installment_date: false,
                    fulfilled_at: false,
                    amount_fulfilled: false,
                },
            }}
            columns={columnHelper => [
                columnHelper.accessor('contact', {
                    id: 'contact_name',
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const contact = info.row.original.contact;

                        if (!contact) {
                            return <EmptyData />;
                        }

                        const { first_name, last_name } = contact;

                        return (
                            <Link
                                className={'text-black'}
                                to={`/accounts/${accountId}/contacts/${contact.id}`}
                            >
                                {first_name} {last_name}
                            </Link>
                        );
                    },
                    meta: {
                        sortType: 'alpha',
                    },
                }),
                columnHelper.accessor('amount', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => (
                        <>
                            <CurrencySymbol />
                            {maskDecimal(info.row.original.amount)}
                        </>
                    ),
                    meta: {
                        sortType: 'numeric',
                    },
                }),
                columnHelper.accessor('amount_remaining', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const item = info.row.original;
                        return (
                            <>
                                <CurrencySymbol />
                                {maskDecimal(item.amount_remaining)}
                            </>
                        );
                    },
                }),
                columnHelper.accessor('amount_fulfilled', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const item = info.row.original;
                        return (
                            <>
                                <CurrencySymbol />
                                {maskDecimal(item?.amount_fulfilled || 0)}
                            </>
                        );
                    },
                    meta: {
                        sortType: 'numeric',
                    },
                }),
                columnHelper.accessor('installment_frequency', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        return (
                            <div>
                                {capitalize(info.row.original.installment_frequency)}{' '}
                                {!!info.row.original.auto_remind && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <FontAwesomeIcon
                                                className="text-gray-500"
                                                icon={faBell}
                                            />
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content inverse>
                                                <Tooltip.Arrow inverse />
                                                <p>Automatic email reminders enabled</p>
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                )}
                            </div>
                        );
                    },
                }),
                columnHelper.accessor('status_friendly', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => (
                        <PledgeStatusIndicator value={info.row.original.status_friendly} />
                    ),
                    enableSorting: false,
                }),
                columnHelper.accessor('created_at', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info =>
                        info.row.original.created_at ? (
                            dateFromServer(info.row.original.created_at).toFormat('LLLL dd, yyyy')
                        ) : (
                            <EmptyData />
                        ),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('next_installment_date', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info =>
                        info.row.original.next_installment_date ? (
                            isoDateFromServer(info.row.original.next_installment_date).toFormat(
                                'LLLL dd, yyyy',
                            )
                        ) : (
                            <EmptyData />
                        ),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('type', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => `${capitalize(info.row.original.type)}`,
                }),
                columnHelper.accessor('first_installment_date', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info =>
                        info.row.original.first_installment_date ? (
                            isoDateFromServer(info.row.original.first_installment_date).toFormat(
                                'LLLL dd, yyyy',
                            )
                        ) : (
                            <EmptyData />
                        ),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('fulfilled_at', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info =>
                        info.row.original.fulfilled_at ? (
                            isoDateFromServer(info.row.original.fulfilled_at).toFormat(
                                'LLLL dd, yyyy',
                            )
                        ) : (
                            <EmptyData />
                        ),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.display({
                    id: Table.Constants.Actions,
                    cell: info => (
                        <PledgeActions.Root key={info.row.original.id} pledge={info.row.original}>
                            <Table.TableRowDropdownTrigger />
                        </PledgeActions.Root>
                    ),
                    meta: {
                        ignoreLink: true,
                    },
                }),
            ]}
            options={{
                enableHiding: true,
                enableSorting: true,
                enablePinning: true,
                enableExpanding: true,
                meta: {
                    enableColumnOrder: true,
                },
            }}
        >
            <PledgesTableProvider>
                <Content>
                    <Pledges />
                </Content>
            </PledgesTableProvider>
        </TableCoreResourceProvider>
    );
};

export { WrappedPledges as Pledges };
